import type React from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { registerPopover } from "./popoverRegistry"
import type { UseDesktopPopoverCoreOptions } from "./useDesktopPopoverCore"
import { useDesktopPopoverCore } from "./useDesktopPopoverCore"

type SingletonItem = {
  content: React.ReactNode
}

type SingletonReferenceProps = React.HTMLAttributes<HTMLElement>

type SingletonOptions = Omit<
  UseDesktopPopoverCoreOptions,
  | "isOpen"
  | "onOpenChange"
  | "referenceElement"
  | "trigger"
  | "getFloatingExtraProps"
> & {
  trigger?: "click" | "mouseenter"
  gracePeriod?: number
}

function normalizeDelay(delay: SingletonOptions["delay"]) {
  if (typeof delay === "number") {
    return { open: delay, close: delay }
  }

  return {
    open: delay?.open ?? 0,
    close: delay?.close ?? 0,
  }
}

export function useSingletonPopover({
  trigger = "mouseenter",
  delay = 0,
  interactive = false,
  gracePeriod = 100,
  ...options
}: SingletonOptions) {
  const [isOpen, setIsOpen] = useState(false)
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null,
  )
  const [content, setContent] = useState<React.ReactNode>(null)

  const openTimeoutRef = useRef<number | null>(null)
  const closeTimeoutRef = useRef<number | null>(null)

  const delays = normalizeDelay(delay)

  const clearOpenTimeout = useCallback(() => {
    if (openTimeoutRef.current != null) {
      window.clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
  }, [])

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current != null) {
      window.clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      if (openTimeoutRef.current != null) {
        window.clearTimeout(openTimeoutRef.current)
      }

      if (closeTimeoutRef.current != null) {
        window.clearTimeout(closeTimeoutRef.current)
      }
    }
  }, [])

  const closePopover = useCallback(
    (immediate = false, useGracePeriod = false) => {
      clearOpenTimeout()

      const run = () => {
        setIsOpen(false)
      }

      const closeDelay = useGracePeriod
        ? Math.max(delays.close, gracePeriod)
        : delays.close

      if (immediate || closeDelay === 0) {
        clearCloseTimeout()
        run()
        return
      }

      clearCloseTimeout()
      closeTimeoutRef.current = window.setTimeout(run, closeDelay)
    },
    [clearOpenTimeout, clearCloseTimeout, delays.close, gracePeriod],
  )

  useEffect(() => {
    return registerPopover(() => {
      closePopover(true)
    })
  }, [closePopover])

  const core = useDesktopPopoverCore({
    ...options,
    isOpen,
    onOpenChange: setIsOpen,
    referenceElement,
    trigger: "manual",
    interactive,
    getFloatingExtraProps:
      interactive && trigger === "mouseenter"
        ? () => ({
            onMouseEnter: () => {
              clearCloseTimeout()
            },
            onMouseLeave: () => {
              closePopover(false, true)
            },
          })
        : undefined,
  })

  const openWithItem = (
    item: SingletonItem,
    element: HTMLElement,
    immediate = false,
  ) => {
    clearCloseTimeout()

    const run = () => {
      setReferenceElement(element)
      core.refs.setReference(element)
      setContent(item.content)
      setIsOpen(true)
    }

    const shouldOpenImmediately = immediate || isOpen || delays.open === 0

    if (shouldOpenImmediately) {
      clearOpenTimeout()
      run()
      return
    }

    clearOpenTimeout()
    openTimeoutRef.current = window.setTimeout(run, delays.open)
  }

  const getReferenceProps = (
    item: SingletonItem,
    userProps: SingletonReferenceProps = {},
  ) => {
    const {
      onMouseEnter,
      onMouseLeave,
      onPointerEnter,
      onPointerLeave,
      onClick,
      ...restUserProps
    } = userProps

    return core.getReferenceProps({
      ...restUserProps,

      onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
        onMouseEnter?.(e)

        if (trigger === "mouseenter") {
          openWithItem(item, e.currentTarget)
        }
      },

      onPointerEnter: (e: React.PointerEvent<HTMLElement>) => {
        onPointerEnter?.(e)
      },

      onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
        onMouseLeave?.(e)

        if (trigger === "mouseenter" && !interactive) {
          closePopover(false, true)
        }
      },

      onPointerLeave: (e: React.PointerEvent<HTMLElement>) => {
        onPointerLeave?.(e)
      },

      onClick: (e: React.MouseEvent<HTMLElement>) => {
        onClick?.(e)

        if (trigger === "click") {
          const sameReference = referenceElement === e.currentTarget

          setReferenceElement(e.currentTarget)
          core.refs.setReference(e.currentTarget)
          setContent(item.content)

          if (sameReference && isOpen) {
            setIsOpen(false)
          } else {
            setIsOpen(true)
          }
        }
      },
    })
  }

  return {
    getReferenceProps,
    renderFloating: () =>
      content == null ? null : core.renderFloating(content),
    close: () => closePopover(true),
    isOpen,
  }
}
